import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, Sparkles, Users, CheckCircle2, AlertCircle, Mail, Phone, MapPin, Globe, Briefcase, Zap, Eye, Database, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Tesseract from 'tesseract.js';

import { extractContactsFromText } from '@/lib/ai/extractContactsFromText.js';
import { processTextWithAI } from '@/lib/ai/BridgeAIProcessor.js';
import mergeContacts from '@/lib/ai/mergeContacts.js';
import { formatPhone } from '@/lib/utils.js';


export function BatchContactWidget() {
  const [stage, setStage] = useState('upload'); // upload, processing, review, results
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [processingIndex, setProcessingIndex] = useState(0);
  const [extractions, setExtractions] = useState([]);
  const [mergedProfiles, setMergedProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [terminalLogs, setTerminalLogs] = useState([]);
  const terminalRef = React.useRef(null);

  const addLog = (message) => {
    setTerminalLogs(prev => [...prev, { time: new Date().toLocaleTimeString(), msg: message }]);
  };

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminalLogs]);


  const startProcessing = async () => {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      setStage('key_missing');
      return;
    }

    setStage('processing');
    setProcessingIndex(0);
    setExtractions([]);
    setMergedProfiles([]);
    setTerminalLogs([]);

    addLog(`🚀 Starting Neural extraction for ${uploadedDocs.length} documents...`);

    const allResults = [];
    let hasCriticalError = false;

    for (let i = 0; i < uploadedDocs.length; i++) {
      if (hasCriticalError) break;
      setProcessingIndex(i);
      addLog(`🧠 AI is reading document [${i + 1}/${uploadedDocs.length}]: ${uploadedDocs[i].name}`);
      
      try {
        const docExtractions = await processTextWithAI(uploadedDocs[i].rawText, apiKey);
        
        if (docExtractions.length > 0) {
          addLog(`✨ Contextual insight found for ${docExtractions.length} people.`);
          docExtractions.forEach(ex => {
            if (ex.name) addLog(`  ↳ Identity: ${ex.name} (${ex.company || 'Private Co'})`);
            ex.sourceDocId = uploadedDocs[i].id;
            ex.sourceDocName = uploadedDocs[i].name;
            ex.emails = ex.email ? [{ value: ex.email }] : [];
            ex.phones = ex.phone ? [{ value: ex.phone }] : [];
          });
          allResults.push(...docExtractions);
          setExtractions(prev => [...prev, ...docExtractions]);
        } else {
          addLog(`⚠ No identifiable entities in ${uploadedDocs[i].name}`);
        }
      } catch (err) {
        const msg = err.message || "";
        if (msg.includes("429")) {
          addLog(`❌ RATE LIMIT: OpenAI account is out of credits or being throttled.`);
          toast.error("OpenAI Rate Limit reached. Please check your billing/usage.", { duration: 5000 });
          hasCriticalError = true;
        } else {
          addLog(`❌ AI processing failed: ${msg.slice(0, 50)}...`);
        }
      }
    }

    if (hasCriticalError) {
      addLog("🛑 Processing halted due to API error.");
      setTimeout(() => setStage('upload'), 3000);
      return;
    }

    addLog('🧬 Running entity resolution and deduplication...');
    await new Promise(resolve => setTimeout(resolve, 800));

    const profiles = mergeContacts(allResults);
    
    // Sort profiles by confidence and name presence
    const sortedProfiles = profiles.sort((a, b) => {
      if (a.name && !b.name) return -1;
      if (!a.name && b.name) return 1;
      return 0;
    });

    addLog(`✨ Successfully resolved ${allResults.length} raw extractions into ${profiles.length} high-fidelity profiles.`);
    setMergedProfiles(sortedProfiles);
    setStage('review');
  };

  const getConfidenceColor = (score) => {
    if (score >= 90) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (score >= 70) return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  };

  const reset = () => {
    setStage('upload');
    setUploadedDocs([]);
    setProcessingIndex(0);
    setExtractions([]);
    setMergedProfiles([]);
    setSelectedProfile(null);
    setEditingProfile(null);
    setEditValues({});
  };

  const startEditing = (profile) => {
    setEditingProfile(profile.id);
    setEditValues({
      name: profile.name,
      title: profile.title,
      company: profile.company,
      email: profile.emails[0]?.value || '',
      phone: profile.phones[0]?.value || '',
      address: profile.address || '',
      website: profile.website || ''
    });
  };

  const saveEdit = () => {
    const updatedProfiles = mergedProfiles.map(p => {
      if (p.id === editingProfile) {
        return {
          ...p,
          name: editValues.name,
          title: editValues.title,
          company: editValues.company,
          address: editValues.address,
          website: editValues.website,
          emails: editValues.email ? [{ value: editValues.email, confidence: 99, sources: p.emails[0]?.sources || [] }] : p.emails,
          phones: editValues.phone ? [{ value: editValues.phone, confidence: 99, sources: p.phones[0]?.sources || [] }] : p.phones,
          fieldConfidence: {
            ...p.fieldConfidence,
            name: 99, title: 99, company: 99, email: 99, phone: 99, address: 99, website: 99
          }
        };
      }
      return p;
    });
    setMergedProfiles(updatedProfiles);
    setEditingProfile(null);
    toast.success('Contact updated');
  };

  const exportFormats = [
    { id: 'csv', label: 'Standard CSV', icon: FileText, desc: 'Excel, Numbers, Sheets' },
    { id: 'vcard', label: 'vCard (.vcf)', icon: Users, desc: 'Outlook, Apple Contacts' },
    { id: 'json', label: 'Global JSON', icon: Zap, desc: 'Full API structure' },
    { id: 'salesforce', label: 'Salesforce CSV', icon: Database, desc: 'Optimized for SFDC' },
    { id: 'hubspot', label: 'HubSpot CSV', icon: Globe, desc: 'Optimized for HubSpot' }
  ];

  const handleGlobalExport = (formatId) => {
    if (mergedProfiles.length === 0) return;

    let content = '';
    let fileName = `bridge_contacts_${new Date().toISOString().split('T')[0]}`;
    let type = 'text/plain';

    switch (formatId) {
      case 'csv':
        const headers = ['First Name', 'Last Name', 'Title', 'Company', 'Email', 'Phone', 'Address', 'Website'];
        const rows = mergedProfiles.map(p => {
          const names = (p.name || '').split(' ');
          const first = names[0] || '';
          const last = names.slice(1).join(' ') || '';
          return [first, last, p.title, p.company, p.emails[0]?.value, p.phones[0]?.value, p.address, p.website];
        });
        content = [headers.join(','), ...rows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
        fileName += '.csv';
        type = 'text/csv';
        break;
      
      case 'json':
        content = JSON.stringify(mergedProfiles, null, 2);
        fileName += '.json';
        type = 'application/json';
        break;

      case 'vcard':
        content = mergedProfiles.map(p => {
          return `BEGIN:VCARD\nVERSION:3.0\nFN:${p.name}\nORG:${p.company}\nTITLE:${p.title}\nEMAIL:${p.emails[0]?.value}\nTEL:${p.phones[0]?.value}\nADR:;;${p.address}\nURL:${p.website}\nEND:VCARD`;
        }).join('\n');
        fileName += '.vcf';
        break;

      case 'salesforce':
        const sfHeaders = ['FirstName', 'LastName', 'Title', 'Company', 'Email', 'Phone', 'Street', 'Website'];
        const sfRows = mergedProfiles.map(p => {
          const names = (p.name || '').split(' ');
          return [names[0] || '', names.slice(1).join(' ') || '', p.title, p.company, p.emails[0]?.value, p.phones[0]?.value, p.address, p.website];
        });
        content = [sfHeaders.join(','), ...sfRows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
        fileName = `salesforce_leads_${Date.now()}.csv`;
        type = 'text/csv';
        break;

      case 'hubspot':
        const hsHeaders = ['firstname', 'lastname', 'jobtitle', 'company', 'email', 'phone', 'address', 'website'];
        const hsRows = mergedProfiles.map(p => {
          const names = (p.name || '').split(' ');
          return [names[0] || '', names.slice(1).join(' ') || '', p.title, p.company, p.emails[0]?.value, p.phones[0]?.value, p.address, p.website];
        });
        content = [hsHeaders.join(','), ...hsRows.map(r => r.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','))].join('\n');
        fileName = `hubspot_contacts_${Date.now()}.csv`;
        type = 'text/csv';
        break;
    }

    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    toast.success(`${formatId.toUpperCase()} export complete`);
  };

  const proceedToExport = () => {
    setStage('results');
  };

  const exportToCSV = () => {
    if (mergedProfiles.length === 0) return;
    const headers = ['Name', 'Title', 'Company', 'Email', 'Phone', 'Address'];
    const rows = mergedProfiles.map(p => [
      p.name, p.title, p.company, 
      p.emails[0]?.value || '', 
      p.phones[0]?.value || '', 
      p.address || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    toast.success('CSV export started');
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const docs = await Promise.all(files.map(async (f, i) => {
      let text = '';
      let isOCR = false;
      if (f.type.startsWith('image/')) {
        toast.loading(`OCR scanning ${f.name}...`);
        const result = await Tesseract.recognize(f, 'eng');
        text = result.data.text;
        isOCR = true;
        toast.dismiss();
      } else {
        text = await f.text();
      }
      return { id: `doc-${Date.now()}-${i}`, name: f.name, rawText: text, isOCR };
    }));

    setUploadedDocs(docs);
  };

  return (
    <div className="w-full bg-slate-950 text-slate-200 rounded-2xl shadow-xl overflow-hidden p-6 md:p-12 border border-slate-800 relative">
        <div className="flex justify-center gap-4 mb-12">
          {['upload', 'processing', 'review', 'results'].map((s, i) => (
            <div key={s} className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${stage === s ? 'bg-blue-500/20 border border-blue-500/40 text-blue-400' : 'bg-slate-800 text-slate-500 opacity-50'}`}>
              <span className="text-sm font-medium uppercase tracking-wider">{s}</span>
            </div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            {stage === 'upload' && (
              <motion.div key="upload" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="rounded-2xl bg-slate-900 border border-slate-700 p-8 text-center">
                <Upload className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Upload Documents</h3>
                <p className="text-slate-400 mb-6">Business cards, signatures, or notes. Images are OCR'd automatically.</p>
                <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} id="file-upload" />
                <div className="flex gap-4 justify-center">
                  <label htmlFor="file-upload" className="px-6 py-3 bg-blue-500 hover:bg-blue-400 text-white font-semibold rounded-xl cursor-pointer transition-all">📁 Choose Files</label>
                </div>
                {uploadedDocs.length > 0 && (
                  <div className="mt-8">
                    <button onClick={startProcessing} className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                      <Sparkles className="w-5 h-5" /> Start AI Extraction
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {stage === 'processing' && (
              <motion.div key="processing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="rounded-2xl bg-slate-900 border border-slate-700 p-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-slate-950 rounded-xl border border-slate-800 p-6 h-64 overflow-y-auto font-mono text-sm text-slate-400">
                    {uploadedDocs[processingIndex]?.rawText || 'Reading...'}
                  </div>
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 flex flex-col justify-between h-64">
                    <div ref={terminalRef} className="overflow-y-auto font-mono text-xs text-blue-400 space-y-1">
                      {terminalLogs.map((log, i) => <div key={i}>[{log.time}] {log.msg}</div>)}
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full mt-4 overflow-hidden">
                       <motion.div className="h-full bg-blue-500" initial={{ width: 0 }} animate={{ width: `${((processingIndex + 1) / uploadedDocs.length) * 100}%` }} />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {stage === 'review' && (
              <motion.div key="review" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden">
                <div className="p-6 border-b border-slate-700 bg-slate-800/30 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Review Extractions</h3>
                  <div className="flex gap-2">
                    <button onClick={exportToCSV} className="px-4 py-2 bg-slate-800 text-white rounded-lg border border-slate-700 hover:bg-slate-700 text-xs">Export CSV</button>
                    <button onClick={proceedToExport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 text-xs">Finalize</button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-left">
                    <thead className="sticky top-0 bg-slate-900 text-slate-500 text-[10px] uppercase font-bold border-b border-slate-800">
                      <tr>
                        <th className="p-4">Contact</th>
                        <th className="p-4">Info</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {mergedProfiles.map(p => (
                        <tr key={p.id} className="hover:bg-slate-800/30">
                          <td className="p-4">
                            <div className="text-white font-bold">{p.name || 'Unknown'}</div>
                            <div className="text-slate-400 text-xs">{p.title} @ {p.company}</div>
                          </td>
                          <td className="p-4 text-xs text-slate-300">
                            <div>{p.emails[0]?.value}</div>
                            <div>{p.phones[0]?.value}</div>
                          </td>
                          <td className="p-4 text-right">
                             <button onClick={() => startEditing(p)} className="p-2 text-slate-500 hover:text-blue-400 transition-colors"><Briefcase className="w-4 h-4"/></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {stage === 'results' && (
              <motion.div key="results" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                {/* Export Format Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
                  {exportFormats.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => handleGlobalExport(f.id)}
                      className="p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-500/50 hover:bg-slate-800/50 transition-all text-center group"
                    >
                      <f.icon className="w-6 h-6 text-blue-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                      <div className="text-[10px] font-bold text-white uppercase mb-1">{f.label}</div>
                      <div className="text-[8px] text-slate-500 leading-tight">{f.desc}</div>
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl bg-slate-900 border border-slate-700 overflow-hidden">
                   <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800/20">
                     <h3 className="text-lg font-bold text-white">Finalized CRM Profiles</h3>
                     <div className="flex gap-4">
                       <button onClick={reset} className="text-xs text-slate-500 hover:text-white font-bold uppercase tracking-widest transition-colors">Discard & Reset</button>
                       <button onClick={() => setStage('review')} className="text-xs text-blue-400 hover:text-blue-300 font-bold uppercase tracking-widest transition-colors">Edit Batch</button>
                     </div>
                   </div>
                   <div className="divide-y divide-slate-800">
                     {mergedProfiles.map(p => (
                       <div key={p.id} className="p-8 hover:bg-slate-800/30 transition-all group">
                         <div className="flex justify-between items-start">
                           <div className="flex-1">
                             <div className="flex items-center gap-3 mb-4">
                               <div className="w-10 h-10 rounded-lg bg-blue-600/10 flex items-center justify-center text-blue-400 font-bold border border-blue-500/20">
                                 {p.name?.[0] || '?'}
                               </div>
                               <div>
                                 <h4 className="font-bold text-white text-lg leading-none mb-1">{p.name || 'Unknown Name'}</h4>
                                 <div className="text-slate-400 text-sm">
                                   {p.title && <span>{p.title}</span>}
                                   {p.title && p.company && <span className="mx-1.5 opacity-30">|</span>}
                                   {p.company && <span>{p.company}</span>}
                                   {!p.title && !p.company && <span className="italic opacity-50 text-xs">No professional info found</span>}
                                 </div>
                               </div>
                             </div>
                             
                             <div className="grid md:grid-cols-3 gap-6">
                               <div className="space-y-2">
                                 <div className="text-[10px] font-bold text-slate-600 uppercase">Communication</div>
                                 <div className="flex items-center gap-2 text-xs text-slate-300"><Mail className="w-3 h-3 text-blue-400"/> {p.emails[0]?.value}</div>
                                 <div className="flex items-center gap-2 text-xs text-slate-300"><Phone className="w-3 h-3 text-emerald-400"/> {formatPhone(p.phones[0]?.value)}</div>
                               </div>
                               <div className="space-y-2">
                                 <div className="text-[10px] font-bold text-slate-600 uppercase">Online</div>
                                 <div className="flex items-center gap-2 text-xs text-slate-300"><Globe className="w-3 h-3 text-slate-500"/> {p.website || 'N/A'}</div>
                                 <div className="flex items-center gap-2 text-xs text-blue-400 hover:underline cursor-pointer"><Zap className="w-3 h-3"/> Verify Social</div>
                               </div>
                               <div className="space-y-2">
                                 <div className="text-[10px] font-bold text-slate-600 uppercase">Location</div>
                                 <div className="flex items-center gap-2 text-xs text-slate-400 leading-relaxed"><MapPin className="w-3 h-3 shrink-0"/> {p.address || 'No address found'}</div>
                               </div>
                             </div>
                           </div>
                           <button 
                             onClick={() => startEditing(p)}
                             className="p-3 text-slate-600 hover:text-white hover:bg-slate-800 rounded-xl transition-all"
                           >
                             <Briefcase className="w-5 h-5"/>
                           </button>
                         </div>
                       </div>
                     ))}
                   </div>
                </div>
              </motion.div>
            )}

            {/* STAGE: API Key Missing */}
            {stage === 'key_missing' && (
              <motion.div 
                key="key_missing" 
                initial={{ opacity: 0, scale: 0.95 }} 
                animate={{ opacity: 1, scale: 1 }} 
                className="rounded-3xl bg-slate-900 border border-amber-500/30 p-12 text-center"
              >
                <div className="w-20 h-20 bg-amber-500/10 rounded-full flex items-center justify-center mx-auto mb-8">
                  <Zap className="w-10 h-10 text-amber-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Neural Brain Offline</h2>
                <p className="text-slate-400 max-w-lg mx-auto mb-10 leading-relaxed">
                  To enable high-fidelity AI extraction, you need to provide an OpenAI API Key.
                  This ensures your data is processed with full contextual awareness.
                </p>
                
                <div className="bg-slate-950 rounded-2xl border border-slate-800 p-8 text-left mb-10">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    How to fix this:
                  </h3>
                  <ol className="space-y-4 text-sm text-slate-400">
                    <li>1. Get a key from <a href="https://platform.openai.com/" target="_blank" className="text-blue-400 hover:underline">platform.openai.com</a></li>
                    <li>2. Open the <code className="text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded">.env</code> file in your project root</li>
                    <li>3. Add this line: <code className="text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded">VITE_OPENAI_API_KEY=your_key_here</code></li>
                    <li>4. Restart your development server</li>
                  </ol>
                </div>

                <button 
                  onClick={() => setStage('upload')}
                  className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all"
                >
                  Return to Upload
                </button>
              </motion.div>
            )}

            {/* STAGE 2: Processing */}
          </AnimatePresence>
        </div>

        {/* Extensive Editor Overlay */}
        <AnimatePresence>
          {editingProfile && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-6">
              <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }} className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl">
                <div className="p-8 border-b border-slate-800 flex justify-between items-center bg-slate-800/20">
                  <div>
                    <h3 className="text-xl font-bold text-white leading-tight">Correct Contact Profile</h3>
                    <p className="text-slate-500 text-xs mt-1 font-bold uppercase tracking-widest">Manual Data Audit</p>
                  </div>
                  <button onClick={() => setEditingProfile(null)} className="p-2 hover:bg-slate-800 rounded-lg text-slate-500 hover:text-white transition-all">✕</button>
                </div>
                <div className="p-8 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Full Name</label>
                      <input type="text" value={editValues.name} onChange={e => setEditValues({...editValues, name: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Name" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Website</label>
                      <input type="text" value={editValues.website} onChange={e => setEditValues({...editValues, website: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Website" />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Job Title</label>
                      <input type="text" value={editValues.title} onChange={e => setEditValues({...editValues, title: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Title" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Company</label>
                      <input type="text" value={editValues.company} onChange={e => setEditValues({...editValues, company: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Company" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Email Address</label>
                      <input type="email" value={editValues.email} onChange={e => setEditValues({...editValues, email: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Email" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Phone Number</label>
                      <input type="text" value={editValues.phone} onChange={e => setEditValues({...editValues, phone: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner" placeholder="Phone" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Physical Address</label>
                    <textarea value={editValues.address} onChange={e => setEditValues({...editValues, address: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white outline-none focus:border-blue-500 transition-all shadow-inner h-24 resize-none" placeholder="Full mailing address..." />
                  </div>
                </div>
                <div className="p-8 bg-slate-950/50 border-t border-slate-800 flex gap-4">
                  <button onClick={() => setEditingProfile(null)} className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl transition-all">Cancel Edits</button>
                  <button onClick={saveEdit} className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98]">Save Verified Profile</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
    </div>
  );
}
